// Правила типографики строки - те же, что у writer при выгрузке (lib/sto_lib.TYPO_RULES: тот же порядок, та же
// область правил, те же замены), чтобы редактор показывал и исправлял то же, что окажется в .docx. Сверка с Python:
// tests/web/test_typography_parity.py (сборка tools/typography-cli.ts). Без зависимостей от редактора (TipTap).
//
// Правила Python переведены с учётом отличий регулярных выражений JS: \w и \b в Python - по Юникоду (кириллица -
// «буква»), в JS - только ASCII, поэтому здесь [\p{L}\p{N}_] и явные просмотры (флаг u). Формула в тексте абзаца -
// символ OBJ (U+FFFC) редактора или U+E001 (как в sto_lib._TextMap), код/ссылка/URL - MASK (U+E000): правка,
// которая их задевает, пропускается. Не перенесены: 'ref_abbr' («рис. 3» -> «рисунке 3»: в редакторе ссылка на
// рисунок - живая, её предлагает inlineLint) и правила только для неразрывных пробелов, кроме «рисунке 3»/«5 кг»
// (при USE_NBSP = false их нет и у writer).
import { MASK, type Edit } from './textmap';
import { NBSP, USE_NBSP } from '../../core/schema/constants';

/** «Неразрывный» пробел типографики: U+00A0 только при USE_NBSP, иначе обычный пробел */
const NB = NBSP;
const EN = '–';
const MINUS = '−';
const W = '\\p{L}\\p{N}_';                                   // \w Python (Юникод) внутри класса символов
const FORMULA = '\\ue001\\ufffc';                            // формула в тексте абзаца (writer / редактор)
const NBW = USE_NBSP ? 'неразрывный пробел' : 'пробел';

export interface TypoCtx {
  /** абзац в ячейке таблицы (sto_lib: без 'math_sign', с 'decimal_cell') */
  cell?: boolean;
  /** описание источника (без правил, меняющих слова) */
  src?: boolean;
  /** знак умножения всей работы: «·» или «×» (multSignForText); без него «*» -> «·», «x» -> «×» */
  mult?: string | null;
}

export interface TypoEdit extends Edit { code: string; msg: string; quiet?: boolean; rule?: string }

type Rep = (m: RegExpExecArray, ctx: TypoCtx) => string;
interface Rule { name: string; code: string; re: RegExp; rep: Rep; msg: string; quiet?: boolean; nbspOnly?: boolean; srcRe?: RegExp }

const rx = (s: string, flags = '') => new RegExp(s, 'gu' + flags);
const str = (s: string): Rep => (m) => s.replace(/\$(\d)/g, (_, k) => m[+k] ?? '');
const isUpper = (s: string) => s === s.toUpperCase() && s !== s.toLowerCase();
const isLower = (s: string) => s === s.toLowerCase() && s !== s.toUpperCase();
const before = (m: RegExpExecArray) => m.input.slice(0, m.index);
const MULT_DOT = '·';
const MULT_CROSS = '×';
const mult = (ctx: TypoCtx, def: string) => (ctx.mult === MULT_DOT || ctx.mult === MULT_CROSS ? ctx.mult : def);

// ------------------------------------------------------------------------------------------------ кавычки
/** Кавычки абзаца (как sto_lib._quote_fix): прямые «"» -> «…»/“…”; внешние английские и немецкие “…”, „…“ -> «…»;
 *  «ёлочки» внутри «ёлочек» -> “…”. Непрямые кавычки переписываются, только если все кавычки абзаца парные. */
export function quoteEdits(text: string): TypoEdit[] {
  const toks: [number, string, boolean][] = [];
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '«' || ch === '„') toks.push([i, ch, true]);
    else if (ch === '»' || ch === '”') toks.push([i, ch, false]);
    else if (ch === '"' || ch === '“') toks.push([i, ch, i === 0 || /[\s(\[{«“„\-–—/￼]/.test(text[i - 1])]);
  }
  let depth = 0;
  let balanced = true;
  for (const [, , op] of toks) { depth += op ? 1 : -1; if (depth < 0) { balanced = false; break; } }
  balanced = balanced && depth === 0;
  const out: TypoEdit[] = [];
  depth = 0;
  for (const [i, ch, op] of toks) {
    if (ch !== '"' && !balanced) { depth = op ? depth + 1 : Math.max(0, depth - 1); continue; }
    let want: string;
    if (op) { want = depth === 0 ? '«' : (ch === '„' ? '„' : '“'); depth++; }
    else { depth = Math.max(0, depth - 1); want = depth === 0 ? '»' : (ch === '“' ? '“' : '”'); }
    if (want !== ch) {
      out.push({ from: i, to: i + 1, insert: want, code: 'TYPO-QUOTES', rule: 'quotes', msg: ch === '"' ? 'Прямые кавычки - в русском тексте «ёлочки», вложенные “лапки”'
        : (want === '«' || want === '»') ? 'Внешние кавычки в русском тексте - «ёлочки»' : 'Вложенные кавычки - “лапки”: «… “…” …»' });
    }
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ данные правил
const U_MULTI = 'мм|см|дм|км|мкм|нм|кг|мг|мс|мкс|нс|мин|сут|Гц|кГц|МГц|ГГц|мВ|кВ|мА|Вт|кВт|МВт|Дж|кДж|Па|кПа|МПа|Ом|кОм|°С|бит|'
  + 'КБ|Кбайт|МБ|Мбайт|ГБ|Гбайт|ТБ|Тбайт|Кбит/с|Мбит/с|Гбит/с|бит/с';
const U_ALL = U_MULTI + '|м|г|т|с|ч|В|А|Н|К|л|Б|%';
const ADJ_END = '(?:ый|ий|ой|ая|яя|ое|ее|ые|ие|ого|его|ому|ему|ым|им|ую|юю|ых|их|ыми|ими|ом|ем)';
const NUM_ADJ_STEMS = 'сегментн|пинов|контактн|битн|ядерн|разрядн|канальн|дюймов|метров|цилиндров|кулачков|градусн|фазн|'
  + 'полюсн|тактн|ступенчат|позиционн|уровнев|кратн|слойн|элементн|этажн|местн|осев|лучев|зубчат|тонн|литров|часов|'
  + 'минутн|секундн|дневн|летн|месячн|процентн|точечн|колесн|угольн|сторонн|гранн|струнн|полосн|портов|потоков|'
  + 'гигабайтн|мегабайтн|вольтов|ваттн|амперн|омн|разов|кнопочн|клавишн|символьн|значн|байтн|строчн|столбцов|рядн|'
  + 'звенн|петлев|контурн|обмоточн|жильн|проводн|ламп|транзисторн';
const ORD_FIX: Record<string, string> = { 'ого': 'го', 'ому': 'му', 'ый': 'й', 'ой': 'й', 'ий': 'й', 'ая': 'я', 'ое': 'е',
  'ые': 'е', 'ых': 'х', 'ым': 'м', 'ыми': 'ми', 'ом': 'м', 'ую': 'ю' };
const UNIT_CASE_BASE: Record<string, string> = { 'дж': 'Дж', 'па': 'Па', 'вт': 'Вт', 'гц': 'Гц', 'ом': 'Ом' };
const MATH_SIGN_WORDS: Record<string, string | null> = { '=': null, '≈': 'приблизительно', '≤': 'не более', '≥': 'не менее',
  '<': 'меньше', '>': 'больше' };
// первая часть составного слова через дефис (как sto_lib.HYPHEN_FIRST): «бизнес-процесс», «блок-схема», «северо-запад»
const HYPHEN_FIRST = new Set(['бизнес', 'интернет', 'веб', 'онлайн', 'офлайн', 'оффлайн', 'медиа', 'вице', 'экс', 'лейб',
  'обер', 'унтер', 'штаб', 'блок', 'пресс', 'фитнес', 'шоу', 'поп', 'рок', 'топ', 'мини', 'макси', 'супер', 'гипер', 'кибер',
  'смарт', 'северо', 'юго', 'ватт', 'киловатт', 'мегаватт', 'вольт', 'киловольт', 'ампер', 'ньютон', 'джоуль', 'кулон',
  'генри', 'тонно', 'человеко', 'машино', 'пассажиро', 'койко', 'грамм', 'килограмм']);
const GOST_PLURAL: Record<string, string> = { 'ов': 'государственных стандартов', 'ы': 'государственные стандарты',
  'ам': 'государственным стандартам', 'ами': 'государственными стандартами', 'ах': 'государственных стандартах' };
const LAT_TWINS = 'AaBCcEeHKMOoPpTXxy';
const CYR_TWINS = 'АаВСсЕеНКМОоРрТХху';

// ------------------------------------------------------------------------------------------------ замены-функции
function translit(w: string, from: string, to: string) {
  return [...w].map((c) => { const i = from.indexOf(c); return i >= 0 ? to[i] : c; }).join('');
}
/** латинские буквы-двойники в русском слове и наоборот («Cоставим», «оff») - как sto_lib._typo_homoglyph */
function homoglyph(m: RegExpExecArray): string {
  const w = m[0];
  if (w.length < 3 || /^[A-Za-z]{1,2}[А-Яа-яЁё]{1,4}$/u.test(w)) return w;
  const lat = w.match(/[A-Za-z]/g) || [];
  const cyr = w.match(/[А-Яа-яЁё]/gu) || [];
  if (cyr.length >= 0.6 * w.length && lat.every((c) => LAT_TWINS.includes(c))) return translit(w, LAT_TWINS, CYR_TWINS);
  if (lat.length >= 0.6 * w.length && cyr.every((c) => CYR_TWINS.includes(c))) return translit(w, CYR_TWINS, LAT_TWINS);
  return w;
}
function dashNames(m: RegExpExecArray): string {
  const pre = before(m);
  const w = /([А-Яа-яЁё]+)$/u.exec(pre);
  return pre.endsWith('»') || (w && isUpper(w[1][0])) ? NB + EN + ' ' : m[0];
}
/** тире без пробелов между словами - как sto_lib._typo_dash_words */
function dashWords(m: RegExpExecArray): string {
  const pre = before(m);
  const post = m.input.slice(m.index + m[0].length);
  if (pre.endsWith('»') || post.startsWith('«')) return NB + EN + ' ';
  const lt = new RegExp(`[${W}\\-]*$`, 'u').exec(pre)![0];
  const rt = new RegExp(`^[${W}]*`, 'u').exec(post)![0];
  if (!/^[А-Яа-яЁё]+$/u.test(lt) || !/^[А-Яа-яЁё]+$/u.test(rt)) return m[0];
  const ll = lt.toLowerCase();
  const rl = rt.toLowerCase();
  if (lt.length === 1 || rt.length === 1) {
    if (lt.length === 1 && isUpper(lt) && rt.length > 1 && /^(?:образн|регулятор|звен)/u.test(rl)) return '-';
    return m[0];
  }
  if (HYPHEN_FIRST.has(ll) || (isUpper(lt) && isLower(rt))) return '-';
  if (/[бвгджзклмнпрстфхцчшщ][оеё]$/u.test(ll) && new RegExp(`^[а-яё]{2,}${ADJ_END}$`, 'u').test(rl)) return '-';
  return NB + EN + ' ';
}
/** дефис с пробелом между словами - как sto_lib._typo_hyphen_space */
function hyphenSpace(m: RegExpExecArray): string {
  const pre = before(m);
  const lt = new RegExp(`[${W}\\-]*$`, 'u').exec(pre)![0];
  if (!/^[А-Яа-яЁё]+$/u.test(lt)) return m[0];
  const ll = lt.toLowerCase();
  // последняя часть ряда с общей второй частью: «альфа-, бета- и гамма- излучения» -> «гамма-излучения»
  const SUSP = '[А-Яа-яЁё]-(?:,|[ \\xa0]+(?:и|или|либо))[ \\xa0]+';
  if (HYPHEN_FIRST.has(ll) || new RegExp(`${SUSP}(?:[А-Яа-яЁё]+-(?:,|[ \\xa0]+(?:и|или|либо))[ \\xa0]+)*${lt}$`, 'u').test(pre)) return '-';
  if (/(?:ие|ия|ье|ья|ьё|ий|ый|ой|ая|яя|ое|ее|ые|ого|его|ому|ему|ть|ться|тся|ешь|ишь)$/u.test(ll)) return NB + EN + ' ';
  return m[0];
}
function gostPlural(m: RegExpExecArray): string {
  let w = GOST_PLURAL[m[1]];
  const pre = before(m);
  if (!pre.trim() || /[.!?][ \xa0]*$/u.test(pre)) w = w[0].toUpperCase() + w.slice(1);
  return w;
}
/** «3х380», «[2x1]» -> «×»; не трогаются формула, набранная текстом («5x2 + 3𝑥2», «f(x) = 2x3» - x переменная),
 *  «PCIe 4.0 x16» (пробел только перед x), «0x1F» (как sto_lib._typo_mult_x). */
function multX(m: RegExpExecArray, ctx: TypoCtx): string {
  const s = m.input;
  if (/[\u{1D400}-\u{1D7FF}]/u.test(s) || (m[2] && !m[4])) return m[0];
  if (/(?<![A-Za-zА-Яа-яЁё])[xX]\d*[ \xa0]*[=+^]|[=+^][ \xa0]*\d*[xX](?![A-Za-zА-Яа-яЁё])|\([ \xa0]*[xX][ \xa0]*[),]/u.test(s)) return m[0];
  return m[1] + m[2] + mult(ctx, MULT_CROSS) + m[4] + m[5];
}
// десятичная точка -> запятая (sto_lib._typo_decimal): не в номерах разделов, версиях, стандартах
const DEC_SKIP = new RegExp('(?:(?<![А-Яа-яЁёA-Za-z])(?:(?:раздел|подраздел|пункт|глав|рисун|таблиц|формул|листинг|приложени|'
  + 'верси|релиз|сборк)[а-яё]*|(?:рис|табл|стр|см|пп|п|разд|гл|ч|гост|ост|сто|ту|сп|снип|iso|iec|ieee|rfc|v|ver|version)'
  + '(?![а-яёa-z]))\\.?|№)[ \\xa0]*\\(?(?:(?:[А-ЯA-Z]\\.)?\\d+(?:\\.\\d+)*[ \\xa0]*(?:[–—\\-]|,|и|или)[ \\xa0]*)*$', 'iu');
function decimal(m: RegExpExecArray): string {
  const s = m[0];
  if (!/\d\.\d/.test(s)) return s;
  const pre = before(m);
  if (DEC_SKIP.test(pre)) return s;
  if (m.groups?.z && /[A-Za-z]{2,}[ \xa0]*$/.test(pre)) return s;          // «Python 0.9» - версия
  return s.replace(/(\d)\.(\d)/g, '$1,$2');
}
const DEC_CELL = /^[\s(]*[±+−–\-]?\d+\.\d+\)?(?:[\s]*[;/][\s]*[±+−–\-]?\d+\.\d+)*[\s)]*$/u;
const YEAR_PREP = /(?:^|[^А-Яа-яЁё])(?:в|во|с|со|до|по|от|к|за|на|около|после|перед|января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря|весной|летом|осенью|зимой)[ \xa0]+$/iu;
function yearWord(m: RegExpExecArray): string {
  if (!m[2].toLowerCase().startsWith('год') && !YEAR_PREP.test(before(m))) return m[0];
  return m[1] + ' г.';
}
function numCase(m: RegExpExecArray): string {
  return m[2] === 'х' && !['2', '3', '4'].includes(m[1]) ? m[0] : m[1];
}
function unitSeries(m: RegExpExecArray): string {
  return m[1] + ((m[1].includes(',') || m[4].includes(',') || m[3] === ';') ? '; ' : ', ');
}
function unitRange(m: RegExpExecArray): string {
  const [a, u, sep, b] = [m[1], m[2], m[3], m[4]];
  if (sep === 'до') return `${a} до ${b} ${u}`;
  if (sep === '…' || sep === '...') return `${a}…${b} ${u}`;
  return a + EN + b + ' ' + u;
}
function unitCase(m: RegExpExecArray): string {
  const [num, pre, base] = m[1] !== undefined ? [m[1], m[2] || '', m[3]] : [m[4], m[5] || '', m[6]];
  return num + NB + pre + UNIT_CASE_BASE[base];
}
function unitMix(m: RegExpExecArray): string {
  const u = ({ 'с': 'с', 'ч': 'ч', 'м': 'мин' } as Record<string, string>)[m[2][0]];
  const after = m.input.slice(m.index + m[0].length);
  const dot = m[3] && (!after.trim() || /^[ \xa0]+[А-ЯЁA-Z]/u.test(after)) ? '.' : '';
  return m[1] + '/' + u + dot;
}
// математические знаки в тексте - словами (sto_lib._typo_math_sign)
const CLAUSE_END = /[;:!?()[\]\n]|[.,](?=[ \xa0]|$)/u;
const REL = /[=≤≥≠≈<>]/u;
function mathSign(m: RegExpExecArray): string {
  const s = m.input;
  const pre = before(m);
  const w = new RegExp(`(?:^|[^${W} \\xa0]|[ \\xa0])([А-Яа-яЁё]+)$`, 'u').exec(pre);
  if (!w || w[1].length < 3) return m[0];
  if (new RegExp(`\\d[ \\xa0]*(?:${U_ALL})$`, 'u').test(pre)) return m[0];     // «100 кОм = 10⁵ Ом»
  let cut = 0;
  for (const x of pre.matchAll(new RegExp(CLAUSE_END.source, 'gu'))) cut = x.index! + x[0].length;
  const left = pre.slice(cut);
  const rest = s.slice(m.index + m[0].length);
  const e = CLAUSE_END.exec(rest.replace(/(\d)[.,](\d)/g, '$1$2'));
  const right = e ? rest.slice(0, e.index) : rest;
  if (REL.test(left) || REL.test(right)) return m[0];                          // несколько знаков - формула
  const signAt = m.index + m[1].length;
  for (const x of s.matchAll(/[=≤≥≠≈<>]/gu)) {
    const b = s.slice(0, x.index);
    if (x.index === signAt || b.split('(').length > b.split(')').length) continue;
    const tok = /(\S+)[ \xa0]*$/u.exec(b);
    if (!tok || !/^[А-Яа-яЁё]{3,}$/u.test(tok[1])) return m[0];               // перечень обозначений величин
  }
  const word = MATH_SIGN_WORDS[m[2]];
  return word == null ? NB + EN + ' ' : ' ' + word + ' ';
}
function numsignWord(m: RegExpExecArray): string {
  const pre = before(m);
  return !pre.trim() || /[.!?]\s*$/u.test(pre) ? 'Номер ' : 'номер ';
}
function decimalCell(m: RegExpExecArray, ctx: TypoCtx): string {
  return ctx.cell && DEC_CELL.test(m.input) ? ',' : m[0];
}

// ------------------------------------------------------------------------------------------------ правила
const UNITS_NB = '(?:байт(?:а|ов|ы)?|бит(?:а|ов|ы)?|[КМГТ]байт(?:а|ов)?|[КкМГТ]Б|[КкМГ]бит/с|бит/с|[КМГТ]иБ|мс|мкс|нс|мин|[кМГ]?Гц|мВ|кВ|мА|к?Вт|[кМ]?Ом|мм|см|км|кг|мг|°[CС]|пт|dpi|px|ГБ|МБ|КБ|м|с|ч|г|В|А|л|т)';
const REF_WORDS = '(?:рисун[а-яё]*|таблиц[а-яё]*|листинг[а-яё]*|формул[а-яё]*|приложени[а-яё]*|раздел[а-яё]*|подраздел[а-яё]*|пункт[а-яё]*|глав[а-яё]*|рис\\.|табл\\.|с\\.|стр\\.|п\\.|пп\\.|гл\\.|разд\\.|ч\\.|т\\.|№|§)';
const NOT_AND = `(?!(?:и|или|либо|а также)(?![${W}]))`;

/** Правила в порядке sto_lib.TYPO_RULES (имя - как там; код - находка sto_check для подсказки в тексте). */
export const RULES: Rule[] = [
  { name: 'double_space', code: 'TYPO-DOUBLE-SPACE', re: rx('(?<=\\S)[ \\u00a0]{2,}(?=\\S)'), msg: 'Двойной пробел',
    rep: (m) => (USE_NBSP && m[0].includes('\u00a0') ? NB : ' ') },
  { name: 'space_before_punct', code: 'TYPO-SPACE-PUNCT', re: rx(`(?<=[^\\s\\ue000])[ \\u00a0]+(?=[,;:!?)»]|\\.(?![${W}.]))`),
    srcRe: rx(`(?<=[^\\s\\ue000])[ \\xa0]+(?=[,!?)»]|\\.(?![${W}.]))`), rep: () => '', msg: 'Пробел перед знаком препинания' },
  { name: 'space_after_open', code: 'TYPO-BRACKET-SPACE', re: rx('(?<=[(«])[ \\u00a0]+'), rep: () => '', msg: 'Пробел после открывающей скобки или кавычки' },
  // подсказку «смешаны алфавиты» в тексте даёт inlineLint - здесь правка без второй подсветки (quiet)
  { name: 'homoglyph', code: 'TYPO-MIXED-ALPHABET', rep: homoglyph, msg: 'Латинские буквы в русском слове (или русские в латинском)', quiet: true,
    re: rx(`(?<![${W}])(?=[A-Za-zА-Яа-яЁё]*[А-Яа-яЁё])(?=[A-Za-zА-Яа-яЁё]*[A-Za-z])[A-Za-zА-Яа-яЁё]+(?![${W}])`) },
  { name: 'hyphen_to_dash', code: 'TYPO-HYPHEN-DASH', rep: () => NB + EN + ' ', msg: 'Дефис вместо тире: « – »',
    re: rx('(?<=[^\\s\\ue000])[ \\u00a0]-{1,2}[ \\u00a0](?=[А-Яа-яЁё«(“„"])|(?<=[А-Яа-яЁё)»”"])[ \\u00a0]-{1,2}[ \\u00a0](?=[^\\s\\ue000])') },
  { name: 'em_dash', code: 'TYPO-EMDASH', re: rx('[ \\u00a0]*—[ \\u00a0]*'), msg: 'Длинное тире «—» → «–» (единый знак тире)',
    rep: (m) => (m[0].replace(/^[ \u00a0]+|[ \u00a0]+$/g, '') === '—' && m[0] !== '—' ? NB + EN + ' ' : EN) },
  { name: 'ellipsis', code: 'TYPO-ELLIPSIS', re: rx('(?<!\\.)\\.\\.\\.(?!\\.)'), rep: () => '…', msg: 'Многоточие - один знак «…»', quiet: true },
  { name: 'dash_nbsp', code: 'TYPO-NBSP-DASH', re: rx('(?<=\\S) (?=–[ \\u00a0])'), rep: () => NB, msg: 'Перед тире - неразрывный пробел', quiet: true, nbspOnly: true },
  { name: 'dash_particle', code: 'TYPO-DASH-NOSPACE', rep: () => '-', msg: 'Перед частицей - дефис: «кто-то», «кое-как»',
    re: rx('(?<=[А-Яа-яЁё])–(?=(?:то|либо|нибудь|таки)(?![А-Яа-яЁё]))|(?<=[Кк]ое)–(?=[а-яё])') },
  { name: 'dash_space', code: 'TYPO-DASH-NOSPACE', rep: () => NB + EN + ' ', msg: 'Тире между словами отбивают пробелами с обеих сторон',
    re: rx('(?:(?<=[А-Яа-яЁё»])|(?<=[^\\d\\s]\\)))[ \\xa0]+–(?=[А-Яа-яЁё«(])|(?<=[А-Яа-яЁё»)])–[ \\xa0]+') },
  { name: 'dash_names', code: 'TYPO-DASH-NOSPACE', rep: dashNames, msg: 'Тире между словами отбивают пробелами с обеих сторон',
    re: rx('(?<=[а-яё])–(?=[А-ЯЁ][а-яё])|(?<=»)–(?=«)') },
  { name: 'dash_words', code: 'TYPO-DASH-NOSPACE', rep: dashWords, re: rx('(?<=[А-Яа-яЁё»])–(?=[А-Яа-яЁё«])'),
    msg: 'Тире между словами отбивают пробелами («ток – напряжение»); в составном слове - дефис («бизнес-процесс»)' },
  { name: 'mult_star', code: 'TYPO-MULT-STAR', re: rx('(?<=\\d)([ \\xa0]*)\\*([ \\xa0]*)(?=\\d)'), msg: 'Звёздочка вместо знака умножения: «25·10»',
    rep: (m, ctx) => m[1] + mult(ctx, MULT_DOT) + m[2] },
  { name: 'exp_minus', code: 'TYPO-MINUS', re: rx('(?:(?<=[·×⋅∙*]10)|(?<=[·×⋅∙*][ \\xa0]10))-(?=\\d)'), rep: () => MINUS,
    msg: 'Показатель степени - со знаком минус: «10−3»' },
  { name: 'mult_x', code: 'TYPO-MULT-X', rep: multX, msg: 'Буква «x» вместо знака умножения: «3×380»',
    re: rx(`(?<![${W}.,])(?!0[xх])(\\d+(?:[.,]\\d+)?)([ \\xa0]*)([xXхХ])([ \\xa0]*)(\\d+(?:[.,]\\d+)?)(?![${W}])`) },
  { name: 'minus_num', code: 'TYPO-MINUS', rep: (m) => (m[1] || '') + MINUS, msg: 'Дефис вместо знака минус перед числом: «−20»',
    re: rx(`(?<![${W}\\-–−/.:§\\ue000${FORMULA}])(?<!\\d )(?<![A-Za-z]{2} )-(?=\\d|∞)|(?<=[=(])([ \\xa0]?)-[ \\xa0]?(?=\\d)`) },
  { name: 'decimal_comma', code: 'TYPO-DECIMAL-POINT', rep: decimal, msg: 'Десятичная запятая: «0,5 мм»',
    re: rx(`(?<![${W}.:/§\\-\\ue000${FORMULA}])(?<u>(?:[±+−–]?\\d+(?:\\.\\d+)?[ \\xa0]*(?:[–—\\-−]|…|\\.\\.\\.|;|,(?=[ \\xa0])|до|и|или)[ \\xa0]*)*`
      + `[±+−–]?\\d+(?:\\.\\d+)?)(?=[ \\xa0]*(?:°|%|(?:${U_MULTI}|сек|ГГц|МГц)(?![${W}])|[мгтсчВАНКлБ](?=[/.,;:)]|[ \\xa0]*$)))`
      + `|(?<![${W}.:/§\\-\\ue000${FORMULA}])(?<z>0\\.\\d+)(?![${W}]|\\.\\d)`) },
  { name: 'decimal_cell', code: 'TYPO-DECIMAL-POINT', re: rx('(?<=\\d)\\.(?=\\d)'), rep: decimalCell, msg: 'Десятичная запятая в ячейке: «0,262»' },
  { name: 'range_spaced', code: 'TYPO-RANGE', rep: str('$1' + EN + '$2'), msg: 'Диапазон - тире без пробелов: «1–4»',
    re: rx(`(?<![${W}.])(\\d+)[ \\u00a0]*[–—][ \\u00a0]*(\\d+)(?![${W}]|\\.\\d)`) },
  { name: 'range_hyphen', code: 'TYPO-RANGE', rep: str('$1' + EN + '$2'), msg: 'Диапазон - через тире без пробелов: «1–4»',
    re: rx(`(?<![${W}.\\-/:\\ue000])(\\d+(?:[.,]\\d+)?)-(\\d+(?:[.,]\\d+)?)(?![${W}\\-/:\\ue000]|\\.\\d)`) },
  { name: 'nbsp_ref', code: 'TYPO-NBSP-REF', rep: (m) => m[1] + NB, msg: 'Неразрывный пробел: «рисунке 3», «№ 5»', quiet: true, nbspOnly: true,
    re: rx('(?<![А-Яа-яЁё])(' + REF_WORDS + ') (?=\\(?\\d|[А-ЯЁ](?![а-яё])|[А-Я]\\.\\d)', 'i') },
  { name: 'numsign_word', code: 'TYPO-SIGN-ALONE', re: rx(`(?<![${W}№])№[ \\xa0]*(?=[а-яё]{3,})`), rep: numsignWord,
    msg: 'Знак «№» без числа - словом: «номер группы» (4.6.2.8)' },
  { name: 'nbsp_numsign', code: 'TYPO-NBSP-NUMSIGN', re: rx('№[ ]?(?=\\d)'), rep: () => '№' + NB, msg: `«№ 5» - через ${NBW}`, quiet: true },
  { name: 'nbsp_unit', code: 'TYPO-NBSP-UNIT', rep: (m) => m[1] + NB + m[2], msg: 'Число и единица - неразрывный пробел', quiet: true, nbspOnly: true,
    re: rx(`(?<![${W}.,])(\\d+(?:[.,]\\d+)?) (${UNITS_NB})(?![${W}/])`) },
  { name: 'percent', code: 'TYPO-PERCENT', re: rx(`(?<![${W}.,])(\\d+(?:[.,]\\d+)?)%`), rep: (m) => m[1] + NB + '%',
    msg: `«50 %» - через ${NBW}`, quiet: true },
  { name: 'abbr_te', code: 'TYPO-ABBR', re: rx('(?<![А-Яа-яЁё])([тТ])\\.[ \\u00a0]?([екпдчЕКПДЧ])\\.?(?![А-Яа-яЁё.])'),
    rep: (m) => m[1] + '.' + NB + m[2] + '.', msg: `«т. е.», «т. к.» - через ${NBW}`, quiet: true },
  { name: 'abbr_i_t', code: 'TYPO-ABBR', re: rx('(?<![А-Яа-яЁё])(и|др)[ ]т\\.(?=\\u00a0[дп]\\.)'), rep: (m) => m[1] + NB + 'т.',
    msg: '«и т. д.» - с неразрывными пробелами', quiet: true, nbspOnly: true },
  { name: 'initials', code: 'TYPO-INITIALS', re: rx(`(?<![${W}.])((?:[A-ZА-ЯЁ]\\.[ \\u00a0]?){1,2})(?=[A-ZА-ЯЁ][a-zа-яё]+)`),
    rep: (m) => (m[1].match(/[A-ZА-ЯЁ]\./gu) || []).join(NB) + NB,
    msg: USE_NBSP ? 'Инициалы и фамилия - неразрывные пробелы' : 'Инициалы - через пробел: «А. Д. Шилов»', quiet: true },
  { name: 'initials_space', code: 'TYPO-INITIALS', re: rx(`(?<![${W}])([А-ЯЁ]\\.)(?=[А-ЯЁ]\\.)`), rep: (m) => m[1] + NB,
    msg: 'Между инициалами - пробел: «С. В.»', quiet: true },
  { name: 'gost_dash', code: 'TYPO-GOST-DASH', rep: str('$1' + EN + '$2'), msg: 'Год в обозначении стандарта - через тире: «ГОСТ 7.32–2017»',
    re: rx(`((?<![${W}])(?:ГОСТ|СТО|ОСТ|СП|СНиП)(?:[ \\u00a0]Р)?[ \\u00a0][\\d.]+)-((?:19|20)\\d{2})(?![${W}])`) },
  { name: 'gost_plural', code: 'TYPO-GOST-NUM', rep: gostPlural, msg: '«ГОСТов» без номера - «государственных стандартов» (4.8.3)',
    re: rx(`(?<![${W}\\-])ГОСТ(ов|ы|ам|ами|ах)(?![${W}\\-])(?![ \\xa0]*(?:Р[ \\xa0]*)?(?:ИСО|ISO|IEC|МЭК|EN)?[ \\xa0/]*\\d)`) },
  { name: 'year_pair', code: 'TYPO-YEAR', rep: str('$1 и $2 гг.'), msg: 'После дат - «гг.»: «2020 и 2021 гг.»',
    re: rx(`(?<![\\d.])((?:1[5-9]|20)\\d{2})[ \\xa0]+и[ \\xa0]+((?:1[5-9]|20)\\d{2})[ \\xa0]+год(?:а|ах|ов|ы)?(?![${W}])\\.?`) },
  { name: 'year_range', code: 'TYPO-YEAR', rep: str('$1 гг.'), msg: 'После диапазона лет - «гг.»: «2010–2012 гг.»',
    re: rx(`(?<![\\d.])((?:1[5-9]|20)\\d{2}[ \\xa0]*[–—\\-][ \\xa0]*(?:1[5-9]|20)\\d{2})[ \\xa0]*(?:г\\.(?![ \\xa0]?г)|год(?:а|ах|ов|ы)(?![${W}])\\.?)`) },
  { name: 'year_gg', code: 'TYPO-YEAR', re: rx('(?<![А-Яа-яЁё])г\\.[ \\xa0]?г\\.'), rep: () => 'гг.', msg: '«гг.» - без точки между буквами' },
  { name: 'year_word', code: 'TYPO-YEAR', rep: yearWord, msg: 'После даты «год» сокращают: «2019 г.»',
    re: rx(`(?<![\\d.,])((?:1[5-9]|20)\\d{2})[ \\xa0]*(год(?:а|у|ом|е)?(?![${W}])|г(?![${W}]))\\.?`) },
  { name: 'ordinal', code: 'TYPO-ORDINAL', rep: (m) => m[1] + '-' + ORD_FIX[m[2]], msg: 'Наращение числительного: «3-й», «5-го»',
    re: rx(`(?<![${W}.])(\\d+)-(ого|ому|ый|ой|ий|ая|ое|ые|ых|ым|ыми|ом|ую)(?![${W}])`) },
  { name: 'ordinal_roman', code: 'TYPO-ORDINAL', rep: str('$1'), msg: 'Римские числа - без наращения: «XIX век»',
    re: rx(`(?<![${W}])([IVXLC]+)-[а-яё]{1,3}(?![${W}])`) },
  { name: 'num_case_adj', code: 'TYPO-COMPOUND-NUM', rep: str('$1-$2'), msg: 'Сложное прилагательное с числом: «8-сегментный»',
    re: rx(`(?<![${W}.])(\\d+)-(?:ми|ти|х|ух|ех|ёх|и)[ \\xa0\\-]?((?:${NUM_ADJ_STEMS})[а-яё]*?${ADJ_END})(?![${W}])`) },
  { name: 'num_case', code: 'TYPO-CARDINAL', rep: numCase, msg: 'Числительное цифрами - без окончания: «из 6 элементов»',
    re: rx('(?<![\\p{L}\\p{N}_.])(\\d+)-(ми|ти|х|ух|ех|ёх|мя|и|ью)(?=[ \\xa0]+[а-яё])(?![ \\xa0]+год)') },
  { name: 'money_mixed', code: 'TYPO-MONEY', rep: str('$1 р. $2 к.'), msg: 'Смешанная сумма: «125 р. 50 к.»',
    re: rx(`(?<![${W}.,])(\\d+)[ \\xa0]*руб(?:лей|ля|ль|\\.)?[ \\xa0]*(\\d+)[ \\xa0]*коп(?:еек|ейки|ейка|ейку)?\\.?`) },
  { name: 'money', code: 'TYPO-MONEY', rep: str('$1 руб.'), msg: 'Денежная единица при числе: «руб.»',
    re: rx(`(\\d)[ \\xa0]*(?:рубл(?:ей|я|ь|ю|ями|ям|ях|ем)(?![${W}])|руб(?![${W}.]))\\.?`) },
  { name: 'kopeks', code: 'TYPO-MONEY', rep: str('$1 коп.'), msg: 'Денежная единица при числе: «коп.»',
    re: rx(`(\\d)[ \\xa0]*(?:копе(?:ек|йки|йка|йку|йками|йкам|йках)(?![${W}])|коп(?![${W}.]))\\.?`) },
  { name: 'thousand', code: 'TYPO-THOUSAND', rep: str('$1 тыс.'), msg: 'При числе - «тыс.»',
    re: rx(`(\\d)[ \\xa0]*(?:тысяч(?:а|и|у|ей|ами|ам|ах|ью)?(?![${W}])|тыс(?![${W}.]))\\.?`) },
  { name: 'million', code: 'TYPO-THOUSAND', rep: str('$1 млн'), msg: 'При числе - «млн» (без точки)',
    re: rx(`(\\d)[ \\xa0]*(?:миллион(?:а|у|ом|е|ы|ов|ами|ам|ах)?(?![${W}])|млн\\.(?=[ \\xa0]*[а-яё\\d,;]))`) },
  { name: 'billion', code: 'TYPO-THOUSAND', rep: str('$1 млрд'), msg: 'При числе - «млрд» (без точки)',
    re: rx(`(\\d)[ \\xa0]*(?:миллиард(?:а|у|ом|е|ы|ов|ами|ам|ах)?(?![${W}])|млрд\\.(?=[ \\xa0]*[а-яё\\d,;]))`) },
  { name: 'deg_angle', code: 'UNIT-DEG', re: rx('(\\d)[ \\xa0]+([°′″])(?![ \\xa0]?[СCF])'), rep: str('$1$2'), msg: 'Градусы угла - без пробела: «45°»' },
  { name: 'deg_c', code: 'UNIT-DEG', re: rx('(\\d)[ \\xa0]?°[ \\xa0]?([СC])(?![А-Яа-яA-Za-z])'), rep: (m) => m[1] + NB + '°' + m[2],
    msg: `«20 °С» - через ${NBW}` },
  { name: 'unit_case', code: 'UNIT-CASE', rep: unitCase, msg: 'Единицы по фамилиям - с прописной: «Вт», «Гц», «кОм»',
    re: rx(`(?<![${W}.,])(\\d+(?:,\\d+)?)[ \\xa0]*(к|М|Г|мк|м)?(дж|па|вт|гц)(?![${W}])|(?<![${W}.,])(\\d+(?:,\\d+)?)[ \\xa0]*(к|М)?(ом)(?![${W}])`) },
  { name: 'unit_mix', code: 'UNIT-MIX', rep: unitMix, msg: 'Не смешивают обозначения и наименования единиц: «км/ч», «мм/с»',
    re: rx(`(\\d[ \\xa0]*(?:км|мм|см|м|Мбит|Кбит|Гбит|МБ|КБ|ГБ|кадр[а-яё]*))[ \\xa0]*/[ \\xa0]*(сек(?:унд[а-яё]*)?|час|мин(?:ут[а-яё]*)?)(?![${W}])(\\.?)`) },
  { name: 'unit_mix_words', code: 'UNIT-MIX', msg: 'Не смешивают обозначения и наименования единиц: «км/ч»',
    rep: (m) => m[1] + '/' + ({ 'ч': 'ч', 'с': 'с', 'м': 'мин' } as Record<string, string>)[m[2][0]],
    re: rx(`(\\d[ \\xa0]*(?:км|Мбит|МБ|ГБ))[ \\xa0]+в[ \\xa0]+(час|секунду|минуту)(?![${W}])`) },
  { name: 'unit_series', code: 'UNIT-SERIES', rep: unitSeries, msg: 'В ряду значений единицу пишут один раз: «1,5; 2,0 мм»',
    re: rx(`(?<![${W}.,])(\\d+(?:,\\d+)?)[ \\xa0]*(${U_MULTI})([;,])[ \\xa0]*(?=([±+−–]?\\d+(?:,\\d+)?)[ \\xa0]*\\2(?![${W}]))`) },
  { name: 'unit_range', code: 'UNIT-RANGE', rep: unitRange, msg: 'В диапазоне единицу пишут один раз: «5–10 мм»',
    re: rx(`(?<![${W}.,])(\\d+(?:,\\d+)?)[ \\xa0]*(${U_ALL})[ \\xa0]*([–—\\-]|…|\\.\\.\\.|до)[ \\xa0]*(\\d+(?:,\\d+)?)[ \\xa0]*\\2(?![${W}])`) },
  { name: 'num_list', code: 'TYPO-NUM-LIST', rep: str('$1; '), msg: 'Дробные числа в ряду - через точку с запятой: «0,5; 1,0»',
    re: rx(`(?<![${W}.,])(\\d+,\\d+),[ \\xa0]+(?=[±+−–]?\\d+,\\d)`) },
  { name: 'div_space', code: 'TYPO-DIV-SPACE', rep: () => '/', msg: 'Косая черта деления - без пробелов: «(a + b)/2»',
    re: rx('(?<=[\\d)])[ \\xa0]+/[ \\xa0]*(?=[\\d(])|(?<=[\\d)])[ \\xa0]*/[ \\xa0]+(?=[\\d(])') },
  { name: 'math_sign', code: 'TYPO-MATH-SIGN', rep: mathSign, msg: 'Математический знак в тексте - словами: «меньше», «не более», « – »',
    re: rx('(?<=[А-Яа-яЁё]{3})([ \\xa0]*)([=≤≥≈]|(?<=[ \\xa0])[<>](?=[ \\xa0]))([ \\xa0]*)(?=[−–\\-]?\\d|(?<![=≈])(?<![=≈][ \\xa0])(?<![=≈][ \\xa0]{2})[а-яё]{3})') },
  { name: 'susp_hyphen', code: 'TYPO-HYPHEN-SPACE', rep: (m) => (m[1] || '') + '-', msg: 'Дефис без пробела: «ПИД-регулятор», «импульсно-фазовый»',
    re: rx(`(?<![${W}\\-])([А-ЯЁA-Z]{2,6})-[ \\xa0]+${NOT_AND}(?=[а-яё]{3,})|(?<=[а-яё]{3}о)-[ \\xa0]+${NOT_AND}(?=[а-яё]{2,}${ADJ_END}(?![${W}]))`) },
  { name: 'hyphen_space', code: 'TYPO-HYPHEN-SPACE', rep: hyphenSpace, msg: 'Дефис с пробелом: между словами - тире « – », в составном слове - дефис',
    re: rx('(?<=[А-Яа-яЁё]{3})-[ \\xa0]+(?!(?:и|или|либо|а также)(?![А-Яа-яЁё]))(?=[а-яё]{2})') },
];

/** Область правил, как sto_lib: без кириллицы - только ANY (и NUMERIC в «числовой» строке); в описании источника -
 *  без NOSRC; в ячейке таблицы - без NOCELL. */
const ANY = new Set(['double_space', 'mult_star', 'exp_minus', 'mult_x', 'space_before_punct', 'space_after_open']);
const NUMERIC = new Set(['minus_num', 'decimal_cell']);
const NOSRC = new Set(['year_pair', 'year_range', 'year_gg', 'year_word', 'money_mixed', 'money', 'kopeks', 'thousand',
  'million', 'billion', 'math_sign', 'decimal_comma', 'num_case_adj', 'num_case', 'unit_range', 'unit_series', 'num_list',
  'ref_abbr', 'numsign_word', 'div_space', 'gost_plural']);
const NOCELL = new Set(['math_sign']);
/** порядок применения: неразрывные -> кавычки -> правила -> крайние пробелы (как sto_lib.typo_text) */
const ORDER = new Map<string, number>([['plain_space', -2], ['quotes', -1], ...RULES.map((r, i) => [r.name, i] as [string, number]), ['edges', RULES.length]]);
const PROTECTED = new RegExp(`[${MASK}${FORMULA}\\n\\t]`, 'u');

/** Правка без общих начала и конца заменяемого и нового текста (как sto_lib._trim_edit). */
function trimEdit(a: number, old: string, ins: string): { from: number; to: number; insert: string } {
  let i = 0;
  const n = Math.min(old.length, ins.length);
  while (i < n && old[i] === ins[i]) i++;
  let j = 0;
  while (j < n - i && old[old.length - 1 - j] === ins[ins.length - 1 - j]) j++;
  return { from: a + i, to: a + old.length - j, insert: ins.slice(i, ins.length - j) };
}

/** Все правки типографики строки блока (индексы строки), по правилам в порядке writer. Правки разных правил могут
 *  пересекаться - resolveEdits выбирает непересекающиеся. */
export function typoEdits(text: string, ctx: TypoCtx = {}): TypoEdit[] {
  const out: TypoEdit[] = [];
  const cyr = /[А-Яа-яЁё]/u.test(text);
  const numeric = !cyr && !/[A-Za-z]{2}/.test(text);
  // неразрывные пробелы и дефис из вставки/импорта -> обычные (writer выгрузит их обычными - sto_lib.USE_NBSP)
  if (!USE_NBSP) {
    for (const m of text.matchAll(/[\u00a0\u202f\u2007\u2011]/gu)) {
      out.push({ from: m.index!, to: m.index! + 1, insert: m[0] === '\u2011' ? '-' : ' ', code: 'TYPO-NBSP-PLAIN', rule: 'plain_space',
        msg: 'Неразрывный пробел - обычным пробелом', quiet: true });
    }
  }
  if (cyr) out.push(...quoteEdits(text));
  for (const r of RULES) {
    if (r.nbspOnly && !USE_NBSP) continue;
    if (!cyr && r.name !== 'double_space' && !ANY.has(r.name) && !(numeric && NUMERIC.has(r.name))) continue;
    if ((ctx.src && NOSRC.has(r.name)) || (ctx.cell && NOCELL.has(r.name))) continue;
    const re = ctx.src && r.srcRe ? r.srcRe : r.re;
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m[0].length === 0) re.lastIndex++;
      const ins = r.rep(m, ctx);
      if (ins === m[0]) continue;
      const e = trimEdit(m.index, m[0], ins);
      if (PROTECTED.test(text.slice(e.from, e.to))) continue;              // код, формула, разрыв строки
      out.push({ ...e, code: r.code, rule: r.name, msg: r.msg, quiet: r.quiet });
    }
  }
  // крайние пробелы (sto_lib: шаг 'edges')
  const lead = /^[ \u00a0\t]+/u.exec(text);
  if (lead && lead[0].length < text.length) out.push({ from: 0, to: lead[0].length, insert: '', code: 'TYPO-EDGE-SPACE', rule: 'edges', msg: 'Пробел в начале абзаца', quiet: true });
  const trail = /[ \u00a0\t]+$/u.exec(text);
  if (trail && trail.index > 0) out.push({ from: trail.index, to: text.length, insert: '', code: 'TYPO-EDGE-SPACE', rule: 'edges', msg: 'Пробел в конце абзаца', quiet: true });
  return out;
}

function overlap(a: Edit, b: Edit): boolean {
  if (a.from < b.to && b.from < a.to) return true;
  if (a.from === a.to) return b.from <= a.from && a.from <= b.to && (b.from === b.to || b.from < a.from && a.from < b.to);
  if (b.from === b.to) return a.from < b.from && b.from < a.to;
  return false;
}

/** Непересекающиеся правки: при пересечении - правило раньше по порядку writer (оно у writer срабатывает первым,
 *  остальное - на следующем проходе по уже исправленному тексту). */
export function resolveEdits<T extends TypoEdit>(edits: T[]): T[] {
  const sorted = [...edits].sort((a, b) => (ORDER.get(a.rule || '') ?? 0) - (ORDER.get(b.rule || '') ?? 0) || a.from - b.from);
  const out: T[] = [];
  for (const e of sorted) if (!out.some((x) => overlap(x, e))) out.push(e);
  return out;
}

/** Применить правки к строке (с конца). */
export function applyToString(text: string, edits: Edit[]): string {
  let s = text;
  for (const e of [...edits].sort((a, b) => b.from - a.from || b.to - a.to)) s = s.slice(0, e.from) + e.insert + s.slice(e.to);
  return s;
}

/** Строка после типографики - как «Исправить типографику» редактора (проходы до неподвижной точки, не больше 8). */
export function typoText(text: string, ctx: TypoCtx = {}, mask?: (s: string) => string): string {
  let s = text;
  for (let pass = 0; pass < 8; pass++) {
    const edits = resolveEdits(typoEdits(mask ? mask(s) : s, ctx));
    if (!edits.length) break;
    s = applyToString(s, edits);
  }
  return s;
}

/** Знак умножения всей работы (4.6.2.4, как sto_lib.mult_sign_for_text): «×», если в тексте и формулах косой крест
 *  встречается чаще точки, иначе «·»; без знаков - по тому, чего больше: «3x380» (крест) или «25*10» (точка). */
export function multSignForText(t: string): string {
  const cross = (t.match(/×/g) || []).length + (t.match(/\\times(?![A-Za-z])/g) || []).length;
  const dot = (t.match(/[·⋅∙]/g) || []).length + (t.match(/\\cdot(?![A-Za-z])/g) || []).length;
  if (cross || dot) return cross > dot ? MULT_CROSS : MULT_DOT;
  const xs = (t.match(new RegExp(`(?<![${W}.])\\d+[ \\xa0]*[xXхХ][ \\xa0]*\\d+(?![${W}])`, 'gu')) || []).length;
  const stars = (t.match(/\d[ \xa0]*\*[ \xa0]*\d/g) || []).length;
  return xs > stars ? MULT_CROSS : MULT_DOT;
}

